import { memo, useMemo } from 'react';
import { Stack, TableCell, TextInput, Tooltip } from '@carbon/react';
import { Information, Launch } from '@carbon/react/icons';
import _ from 'lodash';

import { useIsReadonly } from 'core/readonly';
import OutputsSpacesTable from 'features/outputs/components/OutputsSpacesTable';
import { SPACE_AGGREGATE, SPACE_CATEGORY, SPACE_TYPE_LABEL } from 'models/spaces';
import ModifiedValue from 'shared/components/ModifiedValue';
import { formatArea, formatNumeric, UNSAVED_CELL_PLACEHOLDER } from 'utils/format';
import { convertToCurrentSystem } from 'utils/units';
import styles from 'theme/features/outputs/regular-spaces-table.module.scss';

const SPACE_CATEGORY_KEY_MAP = _.invert(SPACE_CATEGORY);

const HEADERS_BY_SPACE_TYPE = {
  default: [
    { key: 'image', header: 'Image', width: '10%' },
    { key: 'name', header: 'Space Name', width: '20%' },
    { key: 'type', header: 'Enclosed/Open', width: '15%' },
    { key: 'seats', header: 'Seats', width: '7%' },
    { key: 'weightedValue', header: 'Weighted Value', width: '8%' },
    { key: 'unitArea', header: 'Unit Area', width: '8%' },
    { key: 'quantity', header: 'Quantity', width: '10%' },
    { key: 'totalWorkpoints', header: 'Capacity', width: '8%' },
    { key: 'weightedWorkpoints', header: 'Weighted Capacity', width: '8%' },
    { key: 'totalArea', header: 'Total Area', width: '8%' }
  ],
  community: [
    { key: 'image', header: 'Image', width: '10%' },
    { key: 'name', header: 'Space Name', width: '20%' },
    { key: 'type', header: 'Enclosed/Open', width: '15%' },
    { key: 'seats', header: 'Seats', width: '7%' },
    { key: 'weightedValue', header: 'Weighted Value', width: '8%' },
    { key: 'unitArea', header: 'Unit Area', width: '8%' },
    { key: 'quantity', header: 'Quantity', width: '8%' },
    { key: 'totalWorkpoints', header: 'Total Seats', width: '8%' },
    { key: 'weightedWorkpoints', header: 'Weighted Capacity', width: '8%' },
    { key: 'totalArea', header: 'Total Area', width: '8%' }
  ],
  support: [
    { key: 'image', header: 'Image', width: '10%' },
    { key: 'name', header: 'Space Name', width: '25%' },
    { key: 'type', header: 'Enclosed/Open', width: '15%' },
    { key: 'seats', header: 'Seats', width: '10%' },
    { key: 'unitArea', header: 'Unit Area', width: '10%' },
    { key: 'quantity', header: 'Quantity', width: '12%' },
    { key: 'totalWorkpoints', header: 'Total Seats', width: '10%' },
    { key: 'totalArea', header: 'Total Area', width: '10%' }
  ]
};

const getQuantityValue = (isExcluded, isSaved, editsQuantity, outputQuantity) => {
  if (isExcluded) {
    return formatNumeric(0);
  }

  if (editsQuantity != null) {
    return formatNumeric(editsQuantity);
  }

  return isSaved ? formatNumeric(outputQuantity ?? 0) : UNSAVED_CELL_PLACEHOLDER;
};

const getUnitAreaValue = ({ isExcluded, isSaved, editsUnitArea, outputUnitArea, formatter, canEditUnitArea }) => {
  if (isExcluded) {
    return canEditUnitArea && !isSaved ? UNSAVED_CELL_PLACEHOLDER : formatter(outputUnitArea ?? 0);
  }

  if (editsUnitArea != null) {
    return formatter(editsUnitArea);
  }

  return isSaved || !canEditUnitArea ? formatter(outputUnitArea ?? 0) : UNSAVED_CELL_PLACEHOLDER;
};

const RegularSpacesTable = ({
  spaces,
  outputs,
  workstations,
  rowEdits,
  onRowEdit,
  workstationEdits,
  onWorkstationsEdit,
  isShowingCustomizations,
  measurementSystem,
  type = 'default',
  isExport = false,
  project
}) => {
  const isReadonly = useIsReadonly();

  const spacesById = useMemo(() => _.keyBy(spaces, 'id'), [spaces]);

  const data = spaces.map((space) => {
    const output = outputs[space.id];

    const isWorkstation = space.aggregate === SPACE_AGGREGATE.workstation;
    const isCic = space.aggregate === SPACE_AGGREGATE.workstation && !!space.cic;

    const editInfo = rowEdits[space.id];
    const edits = editInfo?.value;
    const isExcluded = edits?.isExcluded;
    const isRowSaved = isWorkstation
      ? isCic
        ? workstationEdits.cic.saved ?? true
        : workstationEdits.other.saved ?? true
      : editInfo?.isSaved ?? true;
    const hasWorkstationEdits = isWorkstation
      ? isCic
        ? workstationEdits.cic.values.length > 0
        : workstationEdits.other.values.length > 0
      : false;

    const canEditUnitArea = space.isCustomArea || space.isCustomOutput;
    const unitArea = getUnitAreaValue({
      isExcluded,
      isSaved: isRowSaved,
      editsUnitArea: edits?.unitArea,
      outputUnitArea: convertToCurrentSystem(output?.unitArea ?? space.unitArea, measurementSystem),
      canEditUnitArea,
      formatter: (value) =>
        formatNumeric(
          space.unitArea == null || Number.isInteger(space.unitArea) ? Math.round(value) : _.round(value, 1)
        )
    });

    return {
      id: space.id,
      minEditArea: space?.minEditArea ?? null,
      image: (
        <Stack gap={4} orientation="horizontal">
          {space.images?.map((image) => (
            <img
              key={image}
              alt=""
              src={`/assets/axons/${SPACE_CATEGORY_KEY_MAP[space.category]}/${image}`}
              height={50}
              width={70}
              className={styles['space-image']}
            />
          ))}
        </Stack>
      ),
      name: (
        <span className={styles['space-name']}>
          {space.url ? (
            <a href={space.url} target="_blank" rel="noreferrer">
              {space.name}
              <span className={styles['space-name__link']}>
                &#xfeff;
                <Launch size={14} />
              </span>
            </a>
          ) : (
            space.name
          )}
          {space.description && (
            <Tooltip align="top-left" label={space.description}>
              <button className={styles.tooltip} type="button">
                <Information />
              </button>
            </Tooltip>
          )}
        </span>
      ),
      type: SPACE_TYPE_LABEL[space.type],
      seats: formatNumeric(output?.seats ?? space.seats ?? 0),
      weightedValue: space.weightedValue?.toString() ?? 'N/A',
      unitArea: (
        <ModifiedValue
          isModified={
            canEditUnitArea &&
            (isExcluded != null || edits?.unitArea != null || (!isRowSaved && output?.unitArea != null))
          }
        >
          {unitArea}
          {space.areaDescription && (
            <Tooltip align="top-left" label={space.areaDescription}>
              <button className={styles.tooltip} type="button">
                <Information />
              </button>
            </Tooltip>
          )}
        </ModifiedValue>
      ),
      quantity: (
        <ModifiedValue
          isModified={
            (isWorkstation && !isRowSaved) ||
            hasWorkstationEdits ||
            isExcluded != null ||
            edits?.quantity != null ||
            (!isRowSaved && output?.quantity == null)
          }
          defaultValue={
            isWorkstation
              ? isCic
                ? formatNumeric(workstations.cic)
                : formatNumeric(workstations.other)
              : output?.defaultQuantity == null
              ? null
              : formatNumeric(output.defaultQuantity)
          }
        >
          {getQuantityValue(isExcluded, isRowSaved, edits?.quantity, output?.quantity)}
        </ModifiedValue>
      ),
      totalWorkpoints: isRowSaved ? formatNumeric(Math.round(output?.capacity ?? 0)) : UNSAVED_CELL_PLACEHOLDER,
      weightedWorkpoints: isRowSaved
        ? formatNumeric(Math.round(output?.weightedCapacity ?? 0))
        : UNSAVED_CELL_PLACEHOLDER,
      totalArea: isRowSaved
        ? formatArea(Math.round(output?.totalArea ?? 0), measurementSystem)
        : UNSAVED_CELL_PLACEHOLDER,
      isCustomOutput: space.isCustomOutput,
      isCustomArea: space.isCustomArea,
      isWorkstation,
      isCic
    };
  });

  const renderEditRow = ({ id, rowEdits, onEdit }) => {
    const space = spacesById[id];
    const isSupportTable = type === 'support';

    return (
      <>
        <TableCell />
        <TableCell />
        <TableCell />
        {isSupportTable && !space?.isCustomOutput && <TableCell />}
        {!isSupportTable && !space?.isCustomOutput && (
          <>
            <TableCell />
            <TableCell />
          </>
        )}
        {space?.isCustomOutput ? (
          <>
            <TableCell>Customize</TableCell>
            <TableCell colSpan={!isSupportTable && space?.isCustomOutput ? 2 : 1}>
              <TextInput
                id={`area-edit-${id}`}
                placeholder="Area"
                type="number"
                labelText="Custom Area"
                onChange={(e) => onEdit('unitArea', e.target.value)}
                value={rowEdits.unitArea ?? ''}
                hideLabel
                readOnly={isReadonly}
                min={0}
              />
            </TableCell>
            <TableCell colSpan={!isSupportTable && space?.isCustomOutput ? 2 : 1}>
              <TextInput
                id={`quantity-edit-${id}`}
                placeholder="Quantity"
                type="number"
                labelText="Custom Quantity"
                onChange={(e) => onEdit('quantity', e.target.value)}
                value={rowEdits.quantity ?? ''}
                readOnly={isReadonly}
                hideLabel
                min={0}
              />
            </TableCell>
          </>
        ) : space?.isCustomArea ? (
          <>
            <TableCell>Custom Area</TableCell>
            <TableCell>
              <TextInput
                id={`area-edit-${id}`}
                type="number"
                labelText="Custom Area"
                onChange={(e) => onEdit('unitArea', e.target.value)}
                value={rowEdits.unitArea ?? ''}
                readOnly={isReadonly}
                hideLabel
                min={0}
              />
            </TableCell>
          </>
        ) : (
          <>
            <TableCell>Custom Quantity</TableCell>
            <TableCell>
              <TextInput
                id={`quantity-edit-${id}`}
                type="number"
                labelText="Custom Quantity"
                onChange={(e) => onEdit('quantity', e.target.value)}
                value={rowEdits.quantity ?? ''}
                readOnly={isReadonly}
                hideLabel
                min={0}
              />
            </TableCell>
          </>
        )}
        <TableCell colSpan={isSupportTable || space?.isCustomOutput ? 2 : 3}>
          <TextInput
            id={`notes-edit-${id}`}
            labelText="Notes"
            placeholder={isReadonly ? '-' : 'Add notes'}
            onChange={(e) => onEdit('notes', e.target.value)}
            readOnly={isReadonly}
            value={rowEdits.notes ?? ''}
            hideLabel
          />
        </TableCell>
      </>
    );
  };

  return (
    <OutputsSpacesTable
      isShowingCustomizations={isShowingCustomizations}
      data={data}
      headers={HEADERS_BY_SPACE_TYPE[type]}
      outputs={outputs}
      renderEditRow={renderEditRow}
      rowEdits={rowEdits}
      onRowEdit={onRowEdit}
      workstationEdits={workstationEdits}
      onWorkstationsEdit={onWorkstationsEdit}
      measurementSystem={measurementSystem}
      isExport={isExport}
      project={project}
      workstations={workstations}
    />
  );
};

export default memo(RegularSpacesTable);
